/* eslint-disable tailwindcss/no-arbitrary-value */
import cn from 'classnames';
import { DayPicker } from 'react-day-picker';
import type { DateRangeFooterState, RangeDayPickerProps } from '../types';
import { DesktopMonthNavigation } from './DesktopMonthNavigation';
import Button from '$/components/Button';
import Info from '$/components/icons/Info';

type DateRangeCalendarProps = {
  dayPickerProps: RangeDayPickerProps;
  desktopDayPickerProps?: {
    left: RangeDayPickerProps;
    right: RangeDayPickerProps;
  };
  isRangeSelected: boolean;
  canGoToPreviousMonth: boolean;
  canGoToNextMonth: boolean;
  onPreviousMonthClick: () => void;
  onNextMonthClick: () => void;
  onConfirmClick: () => void;
  confirmLabel: string;
  isConfirmDisabled: boolean;
  guidanceText?: string;
  footerState: DateRangeFooterState;
  unavailableReasonText?: string;
  previousMonthLabel: string;
  nextMonthLabel: string;
};

export function DateRangeCalendar({
  dayPickerProps,
  desktopDayPickerProps,
  isRangeSelected,
  canGoToPreviousMonth,
  canGoToNextMonth,
  onPreviousMonthClick,
  onNextMonthClick,
  onConfirmClick,
  confirmLabel,
  isConfirmDisabled,
  guidanceText,
  footerState,
  unavailableReasonText,
  previousMonthLabel,
  nextMonthLabel,
}: DateRangeCalendarProps): JSX.Element {
  return (
    <div className="date-range-picker">
      {guidanceText && (
        // Guidance plate at the top: light brand-tint background, no border.
        <div
          aria-live="polite"
          className="mx-[--spacing-padding-x] mt-[--spacing-padding-y-sm] rounded-[8px] px-3 py-2 font-semibold bg-fill/primary-light text-text/primary text-[0.9rem]"
        >
          {guidanceText}
        </div>
      )}
      <div
        className={cn('px-[--spacing-padding-x] py-[--spacing-padding-y-sm]', {
          'range-selected': isRangeSelected,
        })}
      >
        {desktopDayPickerProps ? (
          <div className="relative flex flex-nowrap items-start gap-spacing/padding/section-x-lg">
            <DesktopMonthNavigation
              canGoToNextMonth={canGoToNextMonth}
              canGoToPreviousMonth={canGoToPreviousMonth}
              nextMonthLabel={nextMonthLabel}
              onNextMonthClick={onNextMonthClick}
              onPreviousMonthClick={onPreviousMonthClick}
              previousMonthLabel={previousMonthLabel}
            />
            <DayPicker {...desktopDayPickerProps.left} />
            <DayPicker {...desktopDayPickerProps.right} />
          </div>
        ) : (
          <DayPicker {...dayPickerProps} />
        )}
      </div>
      {unavailableReasonText && (
        // Unavailable-date reason: below the calendars, full width, brand-tint
        // plate with an info icon and border. Sits here (not above the grid) so
        // it never pushes the calendar down.
        <div
          aria-live="polite"
          className="mx-[--spacing-padding-x] mb-[--spacing-padding-y-sm] flex items-center gap-2 rounded-[8px] border border-fill/primary px-3 py-2 bg-fill/primary-light text-text/link text-[0.85rem]"
        >
          <Info aria-hidden className="size-4 shrink-0" />
          <span>{unavailableReasonText}</span>
        </div>
      )}
      <div className="flex items-center justify-between gap-spacing/gap/x-sm border-t border-t-borders/default px-[--spacing-padding-x] py-[--spacing-padding-y-sm]">
        <div className="flex min-w-0 flex-col gap-[2px]">
          <span className="truncate text-[1.05rem]">
            <span
              className={cn(
                footerState.isEmpty
                  ? 'text-text/disabled'
                  : 'text-text/primary',
              )}
            >
              {footerState.rangeText}
            </span>
            {footerState.countText && (
              <span className="ml-1 font-extrabold text-text/primary">
                {footerState.countText}
              </span>
            )}
            {footerState.hintText && (
              <span className="ml-1 text-text/secondary-lighter">
                {footerState.hintText}
              </span>
            )}
          </span>
          <span className="text-text/secondary-lighter text-[0.8rem]">
            {footerState.localTimeText}
          </span>
        </div>
        <Button
          disabled={isConfirmDisabled}
          onClick={onConfirmClick}
          styleType="primary"
          type="button"
          variant="outline"
        >
          {confirmLabel}
        </Button>
      </div>
    </div>
  );
}
